import { defineStore } from 'pinia'
import { api } from '@/composables/api'
import { User } from '../types/User'



export async function assertIsLoggedIn() {


    //check for token in local storage


    if(api.token == null)
    {
       const cache_token = localStorage.getItem('portal-api-token');

       if(cache_token != null)
       {
            api.token = cache_token;
       }
       else
       {
        api.token = '0000'
       }

    }


    try
    {

            const resp = await api.get('/auth/currentUser');
            if(resp.status == 200)
            {
                const authStore = useAuthStore();
                authStore.authUser = new User(resp.data);
                // Fetch role hierarchy if not already loaded
                if (Object.keys(authStore.roleHierarchy).length === 0) {
                    await authStore.fetchRoleHierarchy();
                }
                return true;
            }
            else
            {
                api.token = null;
                localStorage.removeItem('portal-api-token');
                return false;
            }

    }
    catch
    {
        return false
    }



}



export function hasPermission(permission: string) 
{
    return true;
}



export const useAuthStore = defineStore('auth', {
    state: () => ({
        authUser : new User(),
        token: null,
        pendingUsersCount: 0,
        schoolRequestsCount: 0,
        roleHierarchy: {} as Record<string, number>,
        roleDisplayNames: {} as Record<string, string>,
        actualRole: null as string | null // Store the real role when impersonating
    }),
    getters: {
        getAuth(state) {
            return state.authUser;
        },

        // Get available roles for the current user (roles equal to or lower than their own)
        getAvailableRoles(state) {
            // Use actualRole if set (when viewing as lower role), otherwise use authUser.role
            const realRole = state.actualRole || state.authUser.role;
            const currentUserLevel = state.roleHierarchy[realRole] || 0

            // Build role options from hierarchy, excluding root and pending
            const allRoles = Object.keys(state.roleHierarchy)
                .filter(role => role !== 'root' && role !== 'pending')
                .map(role => ({
                    title: state.roleDisplayNames[role] || role,
                    value: role
                }))
                .sort((a, b) => (state.roleHierarchy[b.value] || 0) - (state.roleHierarchy[a.value] || 0))

            return allRoles.filter(role => {
                const roleLevel = state.roleHierarchy[role.value] || 0
                return roleLevel <= currentUserLevel
            })
        }

    },

    actions: {

        async fetchRoleHierarchy() {
            try {
                const response = await api.get('/auth/role-hierarchy');
                if (response.status === 200) {
                    this.roleHierarchy = response.data.hierarchy;
                    this.roleDisplayNames = response.data.displayNames;
                }
            } catch (error) {
                console.error('Error fetching role hierarchy:', error);
                // Fallback to default hierarchy if fetch fails
                this.roleHierarchy = {
                    'root': 100,
                    'admin': 5,
                    'maintainer': 4,
                    'user': 3,
                    'guest': 2,
                    'read-only': 1,
                    'pending': 0
                };
                this.roleDisplayNames = {
                    'root': 'Root',
                    'admin': 'Admin',
                    'maintainer': 'Maintainer',
                    'user': 'User',
                    'guest': 'Guest',
                    'read-only': 'Read-Only',
                    'pending': 'Pending'
                };
            }
        },

        canManageUser(targetUserRole: string): boolean {
            const currentUserLevel = this.roleHierarchy[this.authUser.role] || 0
            const targetUserLevel = this.roleHierarchy[targetUserRole] || 0
            return currentUserLevel >= targetUserLevel
        },

        canAssignRole(roleToAssign: string): boolean {
            const currentUserLevel = this.roleHierarchy[this.authUser.role] || 0
            const roleToAssignLevel = this.roleHierarchy[roleToAssign] || 0
            return roleToAssignLevel <= currentUserLevel
        },

        setImpersonatedRole(role: string | null) {
            // Only root users can impersonate (check actualRole if set, otherwise current role)
            const realRole = this.actualRole || this.authUser.role;
            if (realRole === 'root') {
                if (role === null) {
                    // Restore original role
                    if (this.actualRole) {
                        this.authUser.role = this.actualRole;
                        this.actualRole = null;
                    }
                } else {
                    // Store actual role if not already stored
                    if (!this.actualRole) {
                        this.actualRole = this.authUser.role;
                    }
                    // Override authUser.role with impersonated role
                    this.authUser.role = role;
                }
            }
        },

        clearImpersonation() {
            if (this.actualRole) {
                this.authUser.role = this.actualRole;
                this.actualRole = null;
            }
        },

        async login(credentials: any) {

            const form_data = new FormData();
            form_data.append('username', credentials.username)
            form_data.append('password', credentials.password)
            form_data.append('grand_type', 'password')

            const response = await api.post('/auth/token', form_data);

            if(response.status == 200)
            {

                api.setToken(response.data.access_token);
                localStorage.setItem('portal-api-token', response.data.access_token);
                await assertIsLoggedIn();
                await this.fetchRoleHierarchy();
            }


            return response;
        },

        async fetchAuthUser() {
            const response = await api.get('/auth/currentUser');
            this.authUser = new User(response.data);
        },

        async fetchPendingUsersCount() {
            try {
                const response = await api.get('/users/stats/pending-count');
                if (response.status === 200) {
                    this.pendingUsersCount = response.data.count;
                }
            } catch (error) {
                console.error('Error fetching pending users count:', error);
                this.pendingUsersCount = 0;
            }
        },

        async fetchSchoolRequestsCount() {
            try {
                // Fetch all requests
                const response = await api.get('/requests');
                if (response.status === 200 && response.data) {
                    const requests = response.data;

                    // If user has a school, count incomplete requests for that school
                    if (this.authUser.school) {
                        this.schoolRequestsCount = requests.filter((r: any) =>
                            r.school === this.authUser.school &&
                            r.status !== 'delivered' &&
                            r.status !== 'completed'
                        ).length;
                    } else {
                        // If no school, count all incomplete requests
                        this.schoolRequestsCount = requests.filter((r: any) =>
                            r.status !== 'delivered' &&
                            r.status !== 'completed'
                        ).length;
                    }
                }
            } catch (error) {
                console.error('Error fetching school requests count:', error);
                this.schoolRequestsCount = 0;
            }
        },

        async logout() {
            this.authUser = new User();
            this.token = null;
            this.pendingUsersCount = 0;
            this.schoolRequestsCount = 0;
            api.token = null;
            localStorage.removeItem('portal-api-token');
        },

        async signUpUser(user: any) {
            const response = await api.post('/auth/signup', user);
            return response;
        },

        async changePassword(newPassword: string) {
            const response = await api.post(`/auth/users/${this.authUser.id}/psswd?password=${newPassword}`, {});


            return response;
        }




    },

})